import { Component, NgZone } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { UserService } from './services/user/user.service';
import { delay, filter, tap, take } from 'rxjs/operators';
import { Router, Scroll } from '@angular/router';
import { ViewportScroller } from '@angular/common';
import { PhoneNativeService } from './services/phone-native.service';
import { PageTitlesService } from './services/page-titles.service';
import { TimeoutLogoutService } from './services/timeout-logout.service';
import { PosthogService } from './services/posthog.service';
import * as CookieConsent from 'vanilla-cookieconsent';
import { ConfigService } from './services/json-config.service';
import { EnvironmentKeys } from 'src/environments/environment-keys';
import { Capacitor } from '@capacitor/core';
import { ScreenOrientation } from '@capacitor/screen-orientation';
import { from } from 'rxjs';
import { AppTrackingTransparency } from 'capacitor-plugin-app-tracking-transparency';
import { NativeSafeZoneService } from './services/native-safe-zone.service';
import { LoggerService } from './services/logger/logger.service';
import { LOCAL_STORAGE_KEYS, LocalStorageService } from './services/local-storage.service';
import { App, AppState, URLOpenListenerEvent } from '@capacitor/app';
import { CustomRoutingService } from './services/custom-routing.service';
import { SignalsService } from './services/signals.service';
import { PushNotificationsService } from './services/push-notifications.service';
import { TranslatedConstantsService } from './services/translated-constants.service';
import { PUBLIC_ROUTES } from './shared/constants';
import { ApiDevicePlatforms } from './services/rest-api/rest-api';
import { RedirectService } from './services/redirect.service';
import { LoginService } from './services/login.service';
import { NativeCheckService } from './services/native-check.service';

@Component({
  selector: 'libero-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false
})
export class AppComponent {
  public translationsLoaded = false;
  public viewBiometricOverlay = false;
  public viewNeedUpdateOverlay = false;
  public localDataUpdating = false;
  private appVersionCheckDevice: ApiDevicePlatforms | null = null;

  public get loggedUser():boolean {
    return !!(this.userService.loggedUser);
  }

  public get showAppContent():boolean {
    const showContent =
      !!(this.userService.loggedUser) ||
      this.router.url.includes(PUBLIC_ROUTES.LOGIN)||
      this.router.url.includes(PUBLIC_ROUTES.PUBLIC_INSTRUMENTS) ||
      this.router.url.includes(PUBLIC_ROUTES.DIP_REDIRECT) ||
      this.router.url.includes(PUBLIC_ROUTES.COOKIE_TEXT) ||
      this.router.url.includes(PUBLIC_ROUTES.EMAIL_VERIFICATION) ||
      this.router.url.includes(PUBLIC_ROUTES.ADDITION_REDIRECT) ||
      this.router.url.includes(PUBLIC_ROUTES.CLIENT_ADDITION_REDIRECT) ||
      this.router.url.includes(PUBLIC_ROUTES.BLOG_PREVIEW);

    if(!showContent) {
      setTimeout(() => {
        // todo: refactor with cancellable effect to pprevent stuck on loading
        if(this.router.url === '/') {
          this.router.navigate(['/login']);
        }
      }, 5000);
    }

    return showContent;
  }

  public constructor (
    private translate: TranslateService,
    private userService: UserService,
    private router: Router,
    private viewportScroller: ViewportScroller,
    public phoneNativeService: PhoneNativeService,
    private pageTitlesService: PageTitlesService,
    private timeoutLogoutService: TimeoutLogoutService,
    private posthogService: PosthogService,
    private configService: ConfigService,
    public nativeSafeZoneService: NativeSafeZoneService,
    private loginService: LoginService,
    private zone: NgZone,
    private loggerService: LoggerService,
    private localStorageService: LocalStorageService,
    private customRoutingService: CustomRoutingService,
    private signalsService: SignalsService,
    private pushNotificationsService: PushNotificationsService,
    private translatedConstantsService: TranslatedConstantsService,
    private redirectService: RedirectService,
    private nativeCheckService: NativeCheckService,
  ) {
    this.initDeeplinksWatcher();
    this.initTranslations(translate);
    this.initRouterSubscribe();

    this.checkLocalData();

    if(!this.nativeCheckService.isNativePlatform()) {
      this.initBrowserWorkarounds();
    }

    this.updateUserData();

    if(this.nativeCheckService.isNativePlatform()) {
      this.initNativeAppWorkarounds();
    }
  }

  private initBrowserWorkarounds(): void {
      // watch page on multiple browser panels
      this.initStorageKeyWatcher();
      this.timeoutLogoutService.initIdleTimeoutLogout();
  }

  private initDeeplinksWatcher(): void {
    App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      this.zone.run(() => {
        const slug = event.url.split('.app').pop();
          if (slug) {
              this.router.navigateByUrl(slug);
          }
      });
    });
  }

  private initTranslations(translate: TranslateService): void {
    const language = 'cs';
    // not in v1
    // if(this.userService.loggedUser?.language === 'en-GB') {
    //   language = 'en';
    // }

    translate.addLangs(['cs']);
    translate.setDefaultLang('cs');
    translate.use(language).pipe(
      take(1),
    ).subscribe({
        complete: () => {
          this.translatedConstantsService.initTranslatedConstants();
          this.pageTitlesService.initPageTitles();
          this.translationsLoaded = true;
          this.signalsService.setTranslationsLoaded();

          if(this.nativeCheckService.isNativePlatform()) {
            this.localStorageService.getItem({ key: LOCAL_STORAGE_KEYS.NATIVE_COOKIE_STORAGE_KEY }).pipe(take(1)).subscribe({
              error: this.initCookieFromStorageError.bind(this),
              next: this.initCookieFromStorage.bind(this),
            });
          } else {
            this.initCookieBar();
          }
          if(this.nativeCheckService.isNativePlatform()) {
            if(this.userService.loggedUser) {
              this.phoneNativeService.biometricAuth();
            }
          }
        },
        error: () => {
          this.translationsLoaded = true;
          this.signalsService.setTranslationsLoaded();
        },
    });
  }

  private initCookieFromStorage(ccValue: string): void {
    try {
      if(ccValue && ccValue.length) {
        localStorage.setItem(LOCAL_STORAGE_KEYS.COOKIE_STORAGE_KEY, ccValue);
      }
      this.initCookieBar();
    } catch (error) {
      this.loggerService.error('Error parsing Cookie preferences from storage.', error);
    }
  }

  private initCookieFromStorageError(error: unknown): void {
    this.loggerService.error('Error getting Cookie preferences from storage.', error);
  }

  private initRouterSubscribe(): void {
    this.router.events
    .pipe(filter((e): e is Scroll => e instanceof Scroll))
    .pipe(delay(1))
    .subscribe((e) => {
      if (e.position) {
        this.viewportScroller.scrollToPosition(e.position);
      } else if (e.anchor) {
        this.viewportScroller.scrollToAnchor(e.anchor);
      } else {
        this.viewportScroller.scrollToPosition([0, 0]);
      }
    });
  }

  private initCookieBar(): void {
    CookieConsent.run({
        onConsent: (data) => {
          if (data.cookie.categories.includes(LOCAL_STORAGE_KEYS.COOKIES_ANALYTICS_CATEGORY)) {
            this.initPosthog();
          }
          if(this.nativeCheckService.isNativePlatform()) {
            this.localStorageService.setItem({
              key: LOCAL_STORAGE_KEYS.NATIVE_COOKIE_STORAGE_KEY,
              value: localStorage.getItem(LOCAL_STORAGE_KEYS.COOKIE_STORAGE_KEY) ?? ''
            });
          }
        },
        onChange: (data) => {
          if (data.cookie.categories.includes(LOCAL_STORAGE_KEYS.COOKIES_ANALYTICS_CATEGORY)) {
            this.initPosthog();
          }
          if(this.nativeCheckService.isNativePlatform()) {
            this.localStorageService.setItem({
              key: LOCAL_STORAGE_KEYS.NATIVE_COOKIE_STORAGE_KEY,
              value: localStorage.getItem(LOCAL_STORAGE_KEYS.COOKIE_STORAGE_KEY) ?? ''
            });
          }
        },
        cookie: {
          useLocalStorage: this.nativeCheckService.isNativePlatform(),
        },
        revision: 1,
        disablePageInteraction: true,
        autoClearCookies: true,
        mode: 'opt-in',
        guiOptions: {
          consentModal: {
              layout: 'bar',
              position: 'bottom center',
          },
          preferencesModal: {
              layout: 'box',
              position: 'left',
          }
        },
        categories: {
          necessary: {
            readOnly: true
          },
          analytics: {
            readOnly: false
          }
        },
        language: {
          default: 'cs',
          translations: {
            cs: {
              consentModal: {
                  title: '',
                  description: this.translate.instant('cookieConsent.consentDescription1') + this.configService.getConfig(EnvironmentKeys.cookieConsentDocumentLink) + this.translate.instant('cookieConsent.consentDescription2'),
                  acceptAllBtn: this.translate.instant('cookieConsent.consentAcceptAllBtn'),
                  showPreferencesBtn: this.translate.instant('cookieConsent.consentShowPreferencesBtn'),
              },
              preferencesModal: {
                  title: this.translate.instant('cookieConsent.preferencesTitle'),
                  savePreferencesBtn: this.translate.instant('cookieConsent.preferencesSavePreferencesBtn'),
                  acceptAllBtn: this.translate.instant('cookieConsent.preferencesAcceptAllBtn'),
                  closeIconLabel: this.translate.instant('cookieConsent.preferencesCloseIconLabel'),
                  sections: [
                      {
                          title: '',
                          description: this.translate.instant('cookieConsent.expandedDescription1') + this.configService.getConfig(EnvironmentKeys.cookieConsentDocumentLink) + this.translate.instant('cookieConsent.expandedDescription2'),
                      }, {
                          title: this.translate.instant('cookieConsent.necessaryCookies'),
                          description: this.translate.instant('cookieConsent.necessaryCookiesDescription'),
                          linkedCategory: 'necessary',
                      }, {
                          title: this.translate.instant('cookieConsent.analyticsCookies'),
                          description: this.translate.instant('cookieConsent.analyticsCookiesDescription'),
                          linkedCategory: LOCAL_STORAGE_KEYS.COOKIES_ANALYTICS_CATEGORY,
                      },
                  ]
              },
          },
          }
        },
        onModalReady: (params) => {
          if(params.modalName === 'preferencesModal') {
            params.modal.style.paddingTop = this.nativeSafeZoneService.paddings.top + 'px';
          }
        }
    });
  }

  private initPosthog(): void {
    if(Capacitor.getPlatform() === 'ios') {
      // iOS additional tracking request
      from(AppTrackingTransparency.requestPermission()).pipe(
        take(1),
        tap(response => {
          if(response && response.status === 'authorized') {
            this.posthogService.init();
          }
        }),
      );
    } else {
      this.posthogService.init();
    }
  }

  private initStorageKeyWatcher(): void {
    window.addEventListener('storage', (data) => this.storageChanged(data));
  }

  private storageChanged(data: StorageEvent): void {
    if(!localStorage.getItem(LOCAL_STORAGE_KEYS.TOKEN_LOCAL_STORAGE_KEY) && data.newValue !== data.oldValue) {
      from(this.userService.removeUserFromLocal()).pipe(
        take(1),
        tap(() => {
          this.router.navigate(['/login']);
        })
      ).subscribe();
    }
  }

  /** used to refetch dynamic data on every app load */
  private updateUserData(state?: AppState): void {
    // refetch only if called without state param or when Capacitor called it with isActive
    if(this.userService.loggedUser && (!state || state && state.isActive)) {
      this.loginService.fetchUserData();
    }
  }

  private initNativeAppWorkarounds():void {
    this.phoneNativeService.initListeners();
    this.pushNotificationsService.initNotifications(); // used for token refresh
    App.addListener('appStateChange', (state) => this.updateUserData(state));

    this.phoneNativeService.getBiometryLocked().subscribe({
      next: (flag) => {
        this.zone.run(() => {this.viewBiometricOverlay = flag;})
      }
    });

    if(Capacitor.getPlatform() === 'ios')
      this.appVersionCheckDevice = ApiDevicePlatforms.IOS;
    if(Capacitor.getPlatform() === 'android')
      this.appVersionCheckDevice = ApiDevicePlatforms.ANDROID;

    this.phoneNativeService.isVersionValid(this.appVersionCheckDevice).subscribe({
      next: (validVersion) => {
        this.zone.run(() => this.viewNeedUpdateOverlay = !validVersion);
      }
    });
    ScreenOrientation.lock({orientation: 'portrait'});
    this.customRoutingService.startLoggingHistory();
  }

  private checkLocalData():void {
    this.userService.refreshTokenUpdating().subscribe({
      next: (flag) => {
        this.localDataUpdating = flag;
      }
    });
  }

  public goToAppStore(): void {
    if (this.viewNeedUpdateOverlay && this.appVersionCheckDevice)
      this.redirectService.goToAppStore(this.appVersionCheckDevice);
    else {
      this.loggerService.error(`Invalid state for go to app store to upgrade! viewNeedUpdateOverlay: ${this.viewNeedUpdateOverlay}, appVersionCheckDevice: ${this.appVersionCheckDevice}`);
    }
  }
}
